import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import React, { memo, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { getConsent } from '../../util/consent/consentState';
import { isCordova } from '../../util/mobile/deviceDetection';

const CategoryRowComponent = ({ titleId, descriptionId, checked, disabled = false, onChange }) => (
  <Box alignItems="flex-start" display="flex" justifyContent="space-between" mb={2}>
    <Box pr={2}>
      <Typography fontWeight="bold" id={titleId} variant="body1">
        <FormattedMessage id={titleId} />
      </Typography>
      <Typography color="text.secondary" variant="body2">
        <FormattedMessage id={descriptionId} />
      </Typography>
    </Box>
    <Switch
      checked={checked}
      color="secondary"
      disabled={disabled}
      // MUI v7: `inputProps` is silently overridden by Switch's internal
      // slotProps.input, so the label must go through slotProps. Passing
      // slotProps.input also replaces the internal `role: 'switch'`, so it
      // has to be restated here.
      onChange={onChange}
      slotProps={{ input: { 'aria-labelledby': titleId, role: 'switch' } }}
    />
  </Box>
);
const CategoryRow = memo(CategoryRowComponent);

/**
 * Layer 2 of the cookie banner: per-category toggles.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onDecide called with ({statistics, marketing}, action)
 */
const CookieSettingsDialogComponent = ({ isOpen, onClose, onDecide }) => {
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const stored = getConsent();
    setStatistics(!!stored?.statistics);
    setMarketing(!!stored?.marketing);
  }, [isOpen]);

  const save = () => {
    const action = statistics || marketing ? 'custom' : 'necessary_only';
    onDecide({ statistics, marketing }, action);
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={isOpen}>
      <DialogTitle>
        <FormattedMessage id="CookieConsentV2.dialogTitle" />
      </DialogTitle>
      <DialogContent>
        <Typography mb={3} variant="body2">
          {/* The app has no footer — its re-entry point is the menu (Hilfe section). */}
          <FormattedMessage
            id={
              isCordova() ? 'CookieConsentV2.dialogDescriptionApp' : 'CookieConsentV2.dialogDescription'
            }
          />
        </Typography>
        <CategoryRow
          checked
          descriptionId="CookieConsentV2.categoryNecessaryDescription"
          disabled
          titleId="CookieConsentV2.categoryNecessaryTitle"
        />
        <CategoryRow
          checked={statistics}
          descriptionId={
            isCordova()
              ? 'CookieConsentV2.categoryStatisticsDescriptionApp'
              : 'CookieConsentV2.categoryStatisticsDescription'
          }
          onChange={(e) => setStatistics(e.target.checked)}
          titleId="CookieConsentV2.categoryStatisticsTitle"
        />
        <CategoryRow
          checked={marketing}
          descriptionId="CookieConsentV2.categoryMarketingDescription"
          onChange={(e) => setMarketing(e.target.checked)}
          titleId="CookieConsentV2.categoryMarketingTitle"
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => onDecide({ statistics: false, marketing: false }, 'necessary_only')} size="small" variant="text">
          <FormattedMessage id="CookieConsentV2.necessaryOnly" />
        </Button>
        <Button color="secondary" onClick={save} variant="outlined">
          <FormattedMessage id="CookieConsentV2.save" />
        </Button>
        <Button color="secondary" onClick={() => onDecide({ statistics: true, marketing: true }, 'accept_all')} variant="contained">
          <FormattedMessage id="CookieConsentV2.acceptAll" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(CookieSettingsDialogComponent);
